import { Capacitor } from "@capacitor/core";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AbandonedNotice } from "../components/AbandonedNotice";
import { AppHeader } from "../components/AppHeader";
import { BudgetAlertBanner } from "../components/BudgetAlertBanner";
import { BudgetSuspendedBanner } from "../components/BudgetSuspendedBanner";
import { BuildBadge } from "../components/BuildBadge";
import { CapturesList } from "../components/CapturesList";
import { FeedbackPromptCard } from "../components/FeedbackPromptCard";
import { FinishRecordingCards } from "../components/FinishRecordingCard";
import { PageShell } from "../components/PageShell";
import { PreparingNotice } from "../components/PreparingNotice";
import { ProjectContextBadge } from "../components/ProjectContextBadge";
import { TrashNotice } from "../components/TrashNotice";
import { ActionCard } from "../components/ui/ActionCard";
import { Card } from "../components/ui/Card";
import { Icon } from "../components/ui/Icon";
import { useAiBudgetState } from "../lib/app-config/use-ai-budget-state";
import { useAuth } from "../lib/auth/auth-context";
import { useIsAdmin } from "../lib/auth/use-is-admin";
import { fetchCurrentMeeting } from "../lib/calendar/fetch-meeting";
import { googleAccessToken } from "../lib/calendar/google-auth";
import type { MeetingContext } from "../lib/captures/schema";
import { useCurrentProject } from "../lib/projects/project-context";

const PENDING_MEETING_KEY = "captureBesoins.pendingMeeting";
const PENDING_PROJECT_KEY = "captureBesoins.pendingProjectId";
const PENDING_BATCH_KEY = "captureBesoins.pendingBatchExtraction";

export function setPendingMeeting(meeting: MeetingContext | null): void {
  if (meeting) sessionStorage.setItem(PENDING_MEETING_KEY, JSON.stringify(meeting));
  else sessionStorage.removeItem(PENDING_MEETING_KEY);
}

export function readPendingMeeting(): MeetingContext | null {
  const raw = sessionStorage.getItem(PENDING_MEETING_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as MeetingContext;
  } catch {
    return null;
  }
}

// Step 11 Palier 2 — préférence batch_extraction (default true) lue par
// buildCapture au moment du save. Le choix « pas pressé » se fait désormais à
// l'analyse (écran de fin, admin), pas avant l'enregistrement → plus de setter
// pré-enregistrement ; on retombe simplement sur le défaut batch.
export function readPendingBatchExtraction(): boolean {
  const raw = sessionStorage.getItem(PENDING_BATCH_KEY);
  return raw === null ? true : raw === "1";
}

export function setPendingProjectId(projectId: string | null): void {
  if (projectId) sessionStorage.setItem(PENDING_PROJECT_KEY, projectId);
  else sessionStorage.removeItem(PENDING_PROJECT_KEY);
}

export function readPendingProjectId(): string | null {
  return sessionStorage.getItem(PENDING_PROJECT_KEY);
}

interface HomeCaptureActionsProps {
  isNative: boolean;
  fetchingMeeting: boolean;
  /** Faux tant que le projet courant n'est pas connu. Le garde qui manquait :
   *  sans lui, on ouvrait le micro puis on refusait la capture à l'arrêt —
   *  après que l'audio existe. Refuser au départ coûte une seconde ; refuser à
   *  l'arrivée a coûté une rencontre de 40 minutes le 17 août. */
  projetPret: boolean;
  onIdeeFlash: () => void;
  onRencontre: () => void;
}

// Capture audio = enregistreur natif iOS uniquement. Sur le web, on remplace
// les cartes d'action par une note (pas de fallback MediaRecorder).
function HomeCaptureActions({
  isNative,
  fetchingMeeting,
  projetPret,
  onIdeeFlash,
  onRencontre,
}: HomeCaptureActionsProps) {
  if (!isNative) {
    return (
      <Card>
        <p className="m-0 text-small text-muted">
          La capture audio se fait sur l'app iOS <strong>Écoute</strong>. Sur le web : consultation
          et gestion des captures.
        </p>
      </Card>
    );
  }
  return (
    <>
      {!projetPret && (
        <Card>
          <p className="m-0 text-small text-red-deep">
            Enregistrement indisponible tant qu'aucun projet n'est sélectionné. Touche « Réessayer
            », près du nom du projet en haut de l'écran.
          </p>
        </Card>
      )}
      <div className="grid grid-cols-2 gap-3">
        <ActionCard
          variant="action"
          onClick={onIdeeFlash}
          disabled={!projetPret}
          aria-label="Enregistrer une idée"
          glyph={
            <>
              <Icon name="lightbulb" size={28} />
              <span aria-hidden className="record-glyph record-glyph-sm" />
            </>
          }
          title="Enregistrer une idée"
        />
        <ActionCard
          variant="action"
          onClick={onRencontre}
          disabled={fetchingMeeting || !projetPret}
          aria-label="Enregistrer une rencontre"
          glyph={
            <>
              <Icon name="users" size={28} />
              <span aria-hidden className="record-glyph record-glyph-sm" />
            </>
          }
          title={fetchingMeeting ? "Lecture du calendrier…" : "Enregistrer une rencontre"}
        />
      </div>
    </>
  );
}

export function Home() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const budgetState = useAiBudgetState();
  const isNative = Capacitor.isNativePlatform();
  const [fetchingMeeting, setFetchingMeeting] = useState(false);
  const { currentProjectId } = useCurrentProject();

  // Pose le relais accueil → capture. CaptureScreen le LIT sans l'effacer (il
  // doit survivre à un détour vers les pages légales depuis l'écran de
  // consentement) : c'est donc ici, au départ, qu'on écrase en entier le résidu
  // de la capture précédente — sinon un contexte de rencontre s'accrocherait à
  // l'idée flash suivante.
  function prepareCaptureNavigation() {
    setPendingMeeting(null);
    setPendingProjectId(currentProjectId);
  }

  async function handleRencontre() {
    prepareCaptureNavigation();
    // Démarrage instantané : on enregistre SUR-LE-CHAMP (plus d'écran de
    // préparation). On conserve le pré-remplissage Calendar (si lié) — il
    // alimentera le formulaire de contexte saisi À LA FIN, au moment de lancer
    // la transcription + l'analyse (identification nominative / topics Calendar).
    const token = googleAccessToken(session);
    if (token) {
      setFetchingMeeting(true);
      const r = await fetchCurrentMeeting({ accessToken: token });
      setFetchingMeeting(false);
      if (r.kind === "found") setPendingMeeting(r.meeting);
    }
    navigate("/capture?type=rencontre");
  }

  return (
    <PageShell>
      {/* Hero — la flamme Flambeau comme lueur portée sur le terrain. Le menu
          global (burger, en haut à droite) range la navigation secondaire. */}
      <AppHeader title="Écoute">
        {/* Repère de contexte : tout l'écran (capture + liste) porte sur le
            projet courant. */}
        <ProjectContextBadge className="mt-1 self-start" />
      </AppHeader>

      {/* Actions principales — capture audio sur app iOS uniquement */}
      <HomeCaptureActions
        isNative={isNative}
        fetchingMeeting={fetchingMeeting}
        projetPret={currentProjectId !== null}
        onIdeeFlash={() => {
          prepareCaptureNavigation();
          navigate("/capture?type=idee_flash");
        }}
        onRencontre={handleRencontre}
      />

      {/* Rétroaction testeurs (item A) : « toujours accessible », web ET iOS —
          canal dédié à l'équipe, jamais dans les captures. */}
      <Link to="/retroaction" className="nav-row rounded-md border border-line">
        <Icon name="message-circle" size={18} className="text-muted" />
        <span className="text-small">Donner mon avis</span>
        <Icon name="chevron-down" size={16} className="ml-auto -rotate-90 text-muted" />
      </Link>

      {/* Sollicitation unique (item C) : carte « Comment ça se passe ? »
          après la 3ᵉ capture — l'admissibilité et le refus vivent en base
          (multi-appareils), le composant se masque tout seul sinon. */}
      <FeedbackPromptCard />

      {/* Garde budgétaire (Phase C tracking dépenses IA) : suspension (tous
          usagers) prime sur l'alerte (admin seulement) — jamais les deux à
          la fois, `suspended` implique `alert`. */}
      <BudgetSuspendedBanner state={budgetState} />
      <BudgetAlertBanner state={budgetState} isAdmin={isAdmin} />

      {/* Captures abandonnées par le filet de re-synchro (audio introuvable) :
          notice une fois, au-dessus de la liste, jusqu'au « OK » de l'usager. */}
      <AbandonedNotice />

      {/* Prises abandonnées par « Abandonner l'enregistrement » (incident
          2026-09-04) : à la corbeille locale de 7 jours, notice jusqu'au « OK ». */}
      <TrashNotice />

      {/* Anti-perte audio (phase 1) : prises qui n'existent encore que sur le
          téléphone (« En préparation », « Problème signalé », projet à
          choisir) et enregistrements retrouvés (orphelins, capture tronquée du
          24-09). SOUS les boutons d'enregistrement, jamais entre la personne
          et eux (DS §6.11). */}
      <PreparingNotice />
      <FinishRecordingCards />

      {/* L'accueil EST la liste des captures du projet courant. */}
      <CapturesList />

      <BuildBadge version={__APP_VERSION__} sha={__BUILD_SHA__} date={__BUILD_DATE__} />
    </PageShell>
  );
}
